import type { Trip, TripDay } from "../types";
import { dayCity } from "./trip-route";
import { placeFitsCity } from "./trip-best-day";

/**
 * Moving between cities (owner, 2026-11-04, option A): where a trip changes
 * city, the rough drive between the two, and the stops sitting in a city the
 * day is not in. We sell no rides between cities, so the card offers
 * directions and a WhatsApp request to the team, never a booking.
 *
 * Pure: web and app both import it.
 */

export interface CityPoint {
  readonly lat: number;
  readonly lon: number;
}

export interface CityMove {
  readonly fromCity: string;
  readonly toCity: string;
  /** The first day in the new city; the card sits just before it. */
  readonly dayId: string;
  /** 0-based index of that day. */
  readonly dayIndex: number;
  /** Rough road distance and drive, or null when a city has no position. */
  readonly drive: { readonly km: number; readonly minutes: number } | null;
}

/** Roads wind: straight-line distance times this is close to the road's. */
const ROAD_FACTOR = 1.25;
/** Average speed on Morocco's intercity roads, motorway and national mixed. */
const ROAD_KMH = 75;

function straightKm(a: CityPoint, b: CityPoint): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** The drive between two cities, rounded: km to 10, minutes to 15. Shown as "about". */
export function cityDrive(a: CityPoint, b: CityPoint): { km: number; minutes: number } {
  const road = straightKm(a, b) * ROAD_FACTOR;
  return {
    km: Math.max(10, Math.round(road / 10) * 10),
    minutes: Math.max(15, Math.round(((road / ROAD_KMH) * 60) / 15) * 15),
  };
}

/** Every place the trip changes city, in order. */
export function cityMoves(
  trip: Pick<Trip, "city" | "days">,
  pointOf: (citySlug: string) => CityPoint | null,
): CityMove[] {
  const out: CityMove[] = [];
  trip.days.forEach((day, i) => {
    if (i === 0) return;
    const from = dayCity(trip, trip.days[i - 1]!);
    const to = dayCity(trip, day);
    if (from === to) return;
    const a = pointOf(from);
    const b = pointOf(to);
    out.push({ fromCity: from, toCity: to, dayId: day.id, dayIndex: i, drive: a && b ? cityDrive(a, b) : null });
  });
  return out;
}

/**
 * A stop on a day in another city: the place's city and, when the trip has
 * a day there, the first such day to move it to.
 */
export interface WrongCityStop {
  readonly stopId: string;
  readonly placeCity: string;
  readonly toDayId: string | null;
  readonly toDayIndex: number | null;
}

export function wrongCityStops(
  trip: Pick<Trip, "city" | "days">,
  day: TripDay,
  placeCityOf: (itemId: string) => string | null | undefined,
): WrongCityStop[] {
  const city = dayCity(trip, day);
  const out: WrongCityStop[] = [];
  for (const stop of day.stops) {
    const placeCity = placeCityOf(stop.itemId);
    if (!placeCity || placeFitsCity(placeCity, city)) continue;
    const index = trip.days.findIndex((d) => placeFitsCity(placeCity, dayCity(trip, d)));
    out.push({
      stopId: stop.id,
      placeCity,
      toDayId: index >= 0 ? trip.days[index]!.id : null,
      toDayIndex: index >= 0 ? index : null,
    });
  }
  return out;
}

/** "about 3 h" / "about 2 h 45" / "about 45 min", from rounded minutes. */
export function driveHours(minutes: number): { h: number; m: number } {
  return { h: Math.floor(minutes / 60), m: minutes % 60 };
}
